// client/src/pages/AIReview/AskKeithPage.tsx
// AskKeith — Claude-powered tax research chatbot grounded in the Tax Analysts
// corpus via Azure AI Search. Styling mirrors AIReviewChatPage for consistency.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Send, Plus, MessageSquare, BookOpen, ShieldAlert, ShieldCheck, Shield,
  HelpCircle, Loader, Trash2, Database,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { PERMISSIONS } from '../../types/permissions';
import {
  askKeith, listAskKeithSessions, loadAskKeithSession, deleteAskKeithSession,
  AskKeithAskResponse, AskKeithSessionSummary, AskKeithMessage, AskKeithRef, Confidence,
} from '../../services/askkeith';

// ── Styles (mirror AIReviewChatPage for consistency) ────────────────────────

const s = {
  // Outer 2-column grid — sessions + chat
  wrap:          { display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, alignItems: 'stretch', height: 'calc(100vh - 88px)' } as React.CSSProperties,

  // Side panel
  side:          { display: 'flex', flexDirection: 'column' as const, minHeight: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'rgba(255,255,255,.02)' },
  sideHead:      { padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexShrink: 0 },
  sideTitle:     { fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 },
  sideList:      { flex: 1, overflowY: 'auto' as const, padding: '8px 8px', display: 'flex', flexDirection: 'column' as const, gap: 3 },
  sideEmpty:     { padding: 14, fontSize: 13, color: 'var(--muted)' },
  sideItem:      { padding: '9px 10px', borderRadius: 8, cursor: 'pointer', border: '1px solid transparent', display: 'flex', gap: 6, alignItems: 'flex-start', transition: 'background .12s, border-color .12s' } as React.CSSProperties,
  sideItemActive:{ background: 'rgba(122,162,255,.12)', borderColor: 'rgba(122,162,255,.45)' },
  sideItemLabel: { fontSize: 13, fontWeight: 500, overflow: 'hidden' as const, textOverflow: 'ellipsis' as const, whiteSpace: 'nowrap' as const, flex: 1, minWidth: 0 },
  sideItemMeta:  { fontSize: 11, color: 'var(--muted)', marginTop: 2 },
  sideItemDel:   { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0, opacity: 0.5 },

  // Chat column
  chat:          { display: 'flex', flexDirection: 'column' as const, minHeight: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'rgba(255,255,255,.01)' },
  chatHead:      { padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 },
  chatTitle:     { fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 },
  chatSubtitle:  { fontSize: 12, color: 'var(--muted)' },

  // Messages
  messages:      { flex: 1, overflowY: 'auto' as const, padding: '20px 16px', display: 'flex', flexDirection: 'column' as const, gap: 16 },
  msgRow:        { display: 'flex', gap: 10, alignItems: 'flex-start', maxWidth: 860, width: '100%' } as React.CSSProperties,
  msgRowUser:    { alignSelf: 'flex-end' as const, flexDirection: 'row-reverse' as const },
  msgRowAssist:  { alignSelf: 'flex-start' as const },
  msgAvatar:     { width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, border: '1px solid var(--border)' },
  msgAvatarBot:  { background: 'rgba(122,162,255,.12)', color: 'var(--accent)' },
  msgAvatarUser: { background: 'rgba(255,255,255,.04)', color: 'var(--text)' },
  msgBubble:     { background: 'linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.02))', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 14, lineHeight: 1.6, minWidth: 0, flex: 1 } as React.CSSProperties,
  msgBubbleUser: { background: 'rgba(122,162,255,.16)', border: '1px solid rgba(122,162,255,.35)' },
  msgMetaRow:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' as const },
  msgLabel:      { fontSize: 11, fontWeight: 700, letterSpacing: '.05em', color: 'var(--muted)' },

  // Input bar
  inputBar:      { borderTop: '1px solid var(--border)', padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'flex-end', background: 'rgba(0,0,0,.12)', flexShrink: 0 } as React.CSSProperties,
  textarea:      { flex: 1, padding: '9px 11px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(0,0,0,.18)', color: 'var(--text)', fontSize: 14, resize: 'none' as const, outline: 'none', lineHeight: 1.5, minHeight: 42, maxHeight: 220, fontFamily: 'inherit', overflow: 'auto' as const },
  sendBtn:       { padding: '9px 13px', borderRadius: 10, border: '1px solid rgba(122,162,255,.65)', background: 'rgba(122,162,255,.18)', color: 'var(--accent)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0, alignSelf: 'flex-end' as const, fontSize: 13, fontWeight: 600, fontFamily: 'inherit' } as React.CSSProperties,
  newBtn:        { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(255,255,255,.04)', color: 'var(--muted)', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' } as React.CSSProperties,

  // Empty state
  empty:         { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 18, margin: 'auto', padding: '40px 20px', maxWidth: 720 },
  emptyTitle:    { fontSize: 20, fontWeight: 700 },
  emptySub:      { fontSize: 13, color: 'var(--muted)', textAlign: 'center' as const, lineHeight: 1.55 },
  starterGrid:   { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, width: '100%' },
  starter:       { padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'rgba(255,255,255,.03)', color: 'var(--text)', textAlign: 'left' as const, cursor: 'pointer', fontSize: 13, lineHeight: 1.4, fontFamily: 'inherit', transition: 'all .15s' } as React.CSSProperties,

  // References
  refsWrap:      { marginTop: 12, display: 'flex', flexDirection: 'column' as const, gap: 6 },
  refsHead:      { fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: 'var(--muted)', textTransform: 'uppercase' as const },
  refCard:       { padding: '8px 10px', borderRadius: 8, background: 'rgba(0,0,0,.18)', border: '1px solid var(--border)', fontSize: 12, transition: 'all .15s ease' } as React.CSSProperties,
  refCardActive: { borderColor: 'var(--accent)', background: 'rgba(122,162,255,.08)' },
};

// ── CSS injection: markdown + animations (mirrors AIReviewChatPage pattern) ──

const CSS = `
@keyframes kDotPulse { 0%,80%,100%{opacity:.2} 40%{opacity:1} }
.k-dot{display:inline-block;animation:kDotPulse 1.4s infinite;font-size:22px;letter-spacing:2px;color:var(--muted)}
.k-dot:nth-child(2){animation-delay:.2s}
.k-dot:nth-child(3){animation-delay:.4s}
.k-starter:hover{border-color:rgba(122,162,255,.55)!important;background:rgba(122,162,255,.08)!important}
.k-session-del:hover{opacity:1!important;color:var(--bad)!important}
.k-cite{cursor:pointer;color:var(--accent);margin-left:2px;font-size:.75em;font-weight:600;text-decoration:none}
.k-cite:hover{text-decoration:underline}
.k-md h1,.k-md h2,.k-md h3{font-weight:700;margin:14px 0 6px}
.k-md h2{font-size:1.1em;color:var(--accent);border-bottom:1px solid var(--border);padding-bottom:4px}
.k-md h3{font-size:1em;color:var(--accent2)}
.k-md p{margin:0 0 8px}
.k-md p:last-child{margin-bottom:0}
.k-md ul,.k-md ol{padding-left:1.5em;margin:0 0 8px}
.k-md li{margin:2px 0}
.k-md table{border-collapse:collapse;width:100%;font-size:13px;margin:10px 0}
.k-md th{background:rgba(0,0,0,.18);font-weight:650;color:var(--muted);text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
.k-md th,.k-md td{border:1px solid var(--border);padding:7px 10px}
.k-md tr:nth-child(even) td{background:rgba(255,255,255,.02)}
.k-md code{background:rgba(0,0,0,.25);padding:.1em .35em;border-radius:4px;font-size:85%;color:var(--accent2)}
.k-md pre{background:rgba(0,0,0,.3);border:1px solid var(--border);border-radius:10px;padding:10px 14px;overflow-x:auto}
.k-md pre code{background:none;padding:0;color:var(--text)}
.k-md a{color:var(--accent)}
.k-md blockquote{margin:8px 0;padding:6px 12px;border-left:3px solid var(--accent);color:var(--muted);background:rgba(0,0,0,.12)}
`;

// ── Bits ─────────────────────────────────────────────────────────────────────

function ConfidenceBadge({ level }: { level: Confidence | null }) {
  if (!level) return null;
  const meta: Record<Confidence, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
    high:   { label: 'High confidence',   color: 'var(--good)', bg: 'rgba(87,211,140,.10)',  border: 'rgba(87,211,140,.55)',  icon: <ShieldCheck size={11} /> },
    medium: { label: 'Medium confidence', color: 'var(--warn)', bg: 'rgba(255,209,102,.10)', border: 'rgba(255,209,102,.55)', icon: <Shield size={11} /> },
    low:    { label: 'Low confidence',    color: 'var(--bad)',  bg: 'rgba(255,107,107,.10)', border: 'rgba(255,107,107,.55)', icon: <ShieldAlert size={11} /> },
    none:   { label: 'No supporting sources', color: 'var(--muted)', bg: 'rgba(167,179,196,.08)', border: 'rgba(167,179,196,.45)', icon: <HelpCircle size={11} /> },
    info:   { label: 'Corpus metadata', color: 'var(--accent)', bg: 'rgba(122,162,255,.10)', border: 'rgba(122,162,255,.45)', icon: <Database size={11} /> },
  };
  const m = meta[level];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500,
      color: m.color, background: m.bg, border: `1px solid ${m.border}`,
    }}>
      {m.icon} {m.label}
    </span>
  );
}

function RefsPanel({ refs, highlighted }: { refs: AskKeithRef[]; highlighted: string | null }) {
  if (!refs?.length) return null;
  return (
    <div style={s.refsWrap}>
      <div style={s.refsHead}>References</div>
      {refs.map((r) => (
        <div
          key={r.id}
          id={`ref-${r.id}`}
          style={{ ...s.refCard, ...(highlighted === r.id ? s.refCardActive : {}) }}
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: 'var(--accent)' }}>[{r.id}]</span>
            {r.document_type && <span style={{ color: 'var(--muted)', fontSize: 11 }}>{r.document_type}</span>}
            {r.citation && <span style={{ fontSize: 11 }}>· {r.citation}</span>}
            {r.publication_current_date && (
              <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                · {new Date(r.publication_current_date).toLocaleDateString()}
              </span>
            )}
          </div>
          {r.headline && <div style={{ marginTop: 2, lineHeight: 1.4 }}>{r.headline}</div>}
        </div>
      ))}
    </div>
  );
}

function markdownWithCitations(text: string): string {
  // Convert [^sN] into markdown links whose title starts with `cite:` so we
  // can detect them in the custom `a` renderer below.
  return text.replace(/\[\^s(\d+)\]/g, (_m, n) => `[​](#ref-s${n} "cite:s${n}")`);
}

type Turn = {
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  confidence?: Confidence | null;
  refs?: AskKeithRef[];
  messageId?: number;
};

function AssistantMessage({
  turn, onCiteClick, highlighted,
}: {
  turn: Turn;
  onCiteClick: (refId: string) => void;
  highlighted: string | null;
}) {
  const md = useMemo(() => markdownWithCitations(turn.content), [turn.content]);
  return (
    <div style={{ ...s.msgRow, ...s.msgRowAssist }}>
      <div style={{ ...s.msgAvatar, ...s.msgAvatarBot }}>
        <BookOpen size={13} />
      </div>
      <div style={s.msgBubble}>
        <div style={s.msgMetaRow}>
          <span style={s.msgLabel}>ASKKEITH</span>
          <ConfidenceBadge level={turn.confidence ?? null} />
        </div>
        <div className="k-md">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, title, children, ...rest }) => {
                const match = typeof title === 'string' && title.startsWith('cite:')
                  ? title.slice(5) : null;
                if (!match) return <a href={href} {...rest}>{children}</a>;
                return (
                  <sup
                    className="k-cite"
                    onClick={(e) => { e.preventDefault(); onCiteClick(match); }}
                    title={`Source ${match}`}
                  >[{match}]</sup>
                );
              },
            }}
          >
            {md}
          </ReactMarkdown>
        </div>
        {turn.refs && turn.refs.length > 0 && <RefsPanel refs={turn.refs} highlighted={highlighted} />}
      </div>
    </div>
  );
}

function UserMessage({ turn }: { turn: Turn }) {
  return (
    <div style={{ ...s.msgRow, ...s.msgRowUser }}>
      <div style={{ ...s.msgAvatar, ...s.msgAvatarUser }}>
        <MessageSquare size={13} />
      </div>
      <div style={{ ...s.msgBubble, ...s.msgBubbleUser }}>
        <div style={{ whiteSpace: 'pre-wrap' }}>{turn.content}</div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AskKeithPage() {
  const { ready } = useAuth();
  const perms = usePermissions();
  const navigate = useNavigate();
  const { sessionId: routeSessionId } = useParams<{ sessionId?: string }>();

  const [sessions, setSessions] = useState<AskKeithSessionSummary[]>([]);
  const [turns, setTurns]       = useState<Turn[]>([]);
  const [activeSession, setActiveSession] = useState<string | null>(routeSessionId || null);
  const [composer, setComposer] = useState('');
  const [asking, setAsking]     = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [highlightedRef, setHighlightedRef] = useState<string | null>(null);

  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const textareaRef  = useRef<HTMLTextAreaElement | null>(null);

  const reloadSessions = useCallback(async () => {
    try { setSessions((await listAskKeithSessions(100)).sessions); }
    catch (e: any) { setError(e?.message || String(e)); }
  }, []);

  useEffect(() => {
    if (!ready || !perms.ready) return;
    reloadSessions();
  }, [ready, perms.ready, reloadSessions]);

  useEffect(() => {
    setActiveSession(routeSessionId || null);
    if (!routeSessionId) { setTurns([]); return; }
    let cancelled = false;
    setLoadingSession(true); setError(null);
    loadAskKeithSession(routeSessionId)
      .then((res) => { if (!cancelled) setTurns(res.messages.map(toTurn)); })
      .catch((e) => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoadingSession(false); });
    return () => { cancelled = true; };
  }, [routeSessionId]);

  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns.length, asking]);

  // Auto-grow textarea up to maxHeight
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 220) + 'px';
  }, [composer]);

  async function send() {
    const msg = composer.trim();
    if (!msg || asking) return;
    setAsking(true); setError(null);
    setComposer('');

    const now = new Date().toISOString();
    setTurns((t) => [...t, { role: 'user', content: msg, createdAt: now }]);

    try {
      const res: AskKeithAskResponse = await askKeith({
        sessionId: activeSession || undefined,
        message: msg,
      });
      setTurns((t) => [...t, {
        role: 'assistant',
        content: res.assistantMessage,
        createdAt: new Date().toISOString(),
        confidence: res.confidence,
        refs: res.refs,
        messageId: res.messageId,
      }]);
      if (!activeSession) {
        setActiveSession(res.sessionId);
        navigate(`/ai-review/askkeith/${res.sessionId}`, { replace: true });
      }
      reloadSessions();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setAsking(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }

  function newChat() {
    setActiveSession(null);
    setTurns([]);
    setError(null);
    navigate('/ai-review/askkeith');
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  async function removeSession(id: string) {
    try {
      await deleteAskKeithSession(id);
      if (id === activeSession) newChat();
      reloadSessions();
    } catch (e: any) { setError(e?.message || String(e)); }
  }

  function onCiteClick(refId: string) {
    setHighlightedRef(refId);
    const el = document.getElementById(`ref-${refId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => setHighlightedRef((h) => (h === refId ? null : h)), 2000);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (!ready || !perms.ready) return null;
  if (!perms.hasPermission(PERMISSIONS.NAV_ASK_KEITH)) {
    return (
      <section className="card">
        <div className="hd"><div className="row"><div style={{ fontWeight: 760 }}>AskKeith</div></div></div>
        <div className="bd"><div className="muted">You do not have access to AskKeith. Ask an admin for the <code>nav.ask_keith</code> permission.</div></div>
      </section>
    );
  }

  return (
    <>
      <style>{CSS}</style>
      <div style={s.wrap}>
        {/* Sessions */}
        <aside style={s.side}>
          <div style={s.sideHead}>
            <div style={s.sideTitle}>
              <MessageSquare size={15} /> Conversations
            </div>
            <button style={s.newBtn} onClick={newChat} title="Start a new chat">
              <Plus size={13} /> New
            </button>
          </div>
          <div style={s.sideList}>
            {sessions.length === 0 && <div style={s.sideEmpty}>No conversations yet.</div>}
            {sessions.map((sess) => {
              const active = sess.session_id === activeSession;
              return (
                <div
                  key={sess.session_id}
                  onClick={() => navigate(`/ai-review/askkeith/${sess.session_id}`)}
                  style={{ ...s.sideItem, ...(active ? s.sideItemActive : {}) }}
                  onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,.03)'; }}
                  onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.sideItemLabel}>{sess.title || 'Untitled'}</div>
                    <div style={s.sideItemMeta}>
                      {sess.message_count} msg · {new Date(sess.last_message_at || sess.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="k-session-del"
                    style={s.sideItemDel}
                    title="Delete conversation"
                    onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this conversation?')) removeSession(sess.session_id); }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Chat */}
        <section style={s.chat}>
          <div style={s.chatHead}>
            <div style={s.chatTitle}>
              <BookOpen size={16} style={{ color: 'var(--accent)' }} />
              AskKeith · Tax Research
            </div>
            <div style={s.chatSubtitle}>Grounded in the Tax Analysts corpus. Every claim cites a source.</div>
          </div>

          <div style={s.messages}>
            {loadingSession && <div style={{ color: 'var(--muted)' }}>Loading conversation…</div>}

            {!loadingSession && turns.length === 0 && (
              <EmptyState onPick={(q) => { setComposer(q); setTimeout(() => send(), 0); }} />
            )}

            {turns.map((t, i) =>
              t.role === 'user'
                ? <UserMessage key={i} turn={t} />
                : <AssistantMessage key={i} turn={t} onCiteClick={onCiteClick} highlighted={highlightedRef} />
            )}

            {asking && (
              <div style={{ ...s.msgRow, ...s.msgRowAssist }}>
                <div style={{ ...s.msgAvatar, ...s.msgAvatarBot }}>
                  <BookOpen size={13} />
                </div>
                <div style={{ ...s.msgBubble, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Loader size={14} className="k-dot" style={{ animation: 'spin 1s linear infinite', color: 'var(--accent)' }} />
                  <span style={{ color: 'var(--muted)', fontSize: 13 }}>Researching the corpus…</span>
                </div>
              </div>
            )}

            {error && <div style={{ color: 'var(--bad)', fontSize: 13 }}>Error: {error}</div>}
            <div ref={threadEndRef} />
          </div>

          <div style={s.inputBar}>
            <textarea
              ref={textareaRef}
              style={s.textarea}
              placeholder="Ask a U.S. tax research question… (Enter to send, Shift+Enter for new line)"
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              disabled={asking}
            />
            <button
              style={{ ...s.sendBtn, opacity: asking || !composer.trim() ? 0.5 : 1, cursor: asking || !composer.trim() ? 'not-allowed' : 'pointer' }}
              onClick={send}
              disabled={asking || !composer.trim()}
              title="Send (Enter)"
            >
              <Send size={14} /> Send
            </button>
          </div>
        </section>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ── Empty state / starter prompts ────────────────────────────────────────────

// Starter prompts targeted at documents we know live in the demo corpus
// (from the three sample zips — see Inventory tab). Swap these out once the
// full Tax Analysts feed is ingested.
const STARTERS = [
  'What does recent IRS guidance say about the Section 163(j) business interest deduction limitation?',
  'Summarize the Section 301.7216 rules on disclosure or use of tax return information.',
  'What are the final regulations on substantiation requirements for nonpersonal use vehicles?',
  'What did the recent court rulings say about return preparers and disgorgement of fees?',
];

function EmptyState({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div style={s.empty}>
      <BookOpen size={34} style={{ color: 'var(--accent)', opacity: 0.85 }} />
      <div style={{ textAlign: 'center' }}>
        <div style={s.emptyTitle}>AskKeith is ready</div>
        <div style={s.emptySub}>
          I answer U.S. tax research questions using the Tax Analysts corpus. Every substantive claim comes with a citation. I'll redirect non-tax questions and decline if the corpus doesn't support an answer.
        </div>
      </div>
      <div style={s.starterGrid}>
        {STARTERS.map((q) => (
          <button key={q} className="k-starter" style={s.starter} onClick={() => onPick(q)}>
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Helper: row → Turn ───────────────────────────────────────────────────────

function toTurn(m: AskKeithMessage): Turn {
  if (m.role === 'assistant') {
    const refs = m.retrieval_snapshot?.refs || [];
    return {
      role: 'assistant',
      content: m.content,
      createdAt: m.created_at,
      confidence: m.confidence_level,
      refs,
      messageId: m.message_id,
    };
  }
  return { role: m.role as 'user', content: m.content, createdAt: m.created_at };
}
